import axios from "axios";

/**
 * Vuex module for managing platform data.
 *
 * This module provides state, mutations, and actions for handling platform data,
 * including loading global and custom platforms, updating platform information,
 * and retrieving platform data.
 */
export default {
  namespaced: true,

  /**
   * The state object for the Platforms Vuex module.
   *
   * This state object contains the following properties:
   *
   * - `platforms`: An array of platform objects,
   *    representing the global and custom platforms loaded for the application.
   */
  state: {
    platforms: [],
  },

  /**
   * Mutations for the Platforms Vuex module.
   *
   * These mutations handle updating the state of the Platforms module, including:
   * - Setting the initial list of platforms
   * - Updating a specific platform's data
   * - Updating a specific platform's quote information
   * - Updating a specific field on a platform
   */
  mutations: {
    SET_PLATFORMS(state, platforms) {
      state.platforms = platforms.map(platform => ({
        ...platform,
        valuationsAUM: '',
        transactionAUM: '',
        quoteResult: null,
        valuationsQuote: 0,
        transactionsQuote: 0,
        historyQuote: 0,
        credentials: (platform.id === 'aj-bell' || platform.id === 'transact') ? [{ username: '', password: '' }] : null,
        credentials_valid: true,
      }));
    },
    UPDATE_PLATFORM(state, { id, changes }) {
      const platform = state.platforms.find((p) => p.id === id);
      if (platform) {
        Object.assign(platform, changes);
      }
    },
    UPDATE_PLATFORM_QUOTE(
      state,
      { id, valuationsAUM, transactionAUM, quoteResult, valuationsQuote, transactionsQuote, historyQuote }
    ) {
      const platform = state.platforms.find((p) => p.id === id);
      if (platform) {
        platform.valuationsAUM = valuationsAUM;
        platform.transactionAUM = transactionAUM;
        platform.quoteResult = quoteResult;
        platform.valuationsQuote = valuationsQuote;
        platform.transactionsQuote = transactionsQuote;
        platform.historyQuote = historyQuote;
      }
    },
    UPDATE_PLATFORM_FIELD(state, { id, field, value }) {
      const platform = state.platforms.find((p) => p.id === id);
      if (platform) {
        platform[field] = value;
      }
    },
  },

  /**
   * Actions for the Platforms Vuex module.
   *
   * These actions handle the following functionality:
   * - Initializing the list of platforms by loading global and custom platforms
   * - Updating a specific platform's data
   * - Updating a specific platform's quote information
   * - Updating a specific field on a platform
   */
  actions: {
    async initializePlatforms({ commit }) {
      try {
        // Load global platforms
        const globalResponse = await axios.get(
          "/global/data/global_platforms.json"
        );
        let platforms = globalResponse.data;

        // Try to load custom platforms
        try {
          const customResponse = await axios.get(
            "data/customise_platforms.json"
          );
          const customPlatforms = customResponse.data;

          // Merge custom platforms with global platforms
          platforms = platforms.map((platform) => {
            const customPlatform = customPlatforms.find(
              (cp) => cp.id === platform.id
            );
            return customPlatform
              ? { ...platform, ...customPlatform }
              : platform;
          });
        } catch (error) {
          console.log("No custom platforms found, using global platforms only");
        }

        commit("SET_PLATFORMS", platforms);
      } catch (error) {
        console.error("Error loading platforms:", error);
        commit("SET_PLATFORMS", []);
      }
    },
    updatePlatform({ commit }, { id, changes }) {
      commit("UPDATE_PLATFORM", { id, changes });
    },
    updatePlatformQuote(
      { commit },
      { id, valuationsAUM, transactionAUM, quoteResult, valuationsQuote, transactionsQuote, historyQuote }
    ) {
      commit("UPDATE_PLATFORM_QUOTE", {
        id,
        valuationsAUM,
        transactionAUM,
        quoteResult,
        valuationsQuote,
        transactionsQuote,
        historyQuote,
      });
    },
    updatePlatformField({ commit }, { id, field, value }) {
      commit("UPDATE_PLATFORM_FIELD", { id, field, value });
    },
  },

  /**
   * Getters for the Platforms Vuex module.
   *
   * These getters provide the following functionality:
   * - `getPlatformById`: Returns a platform object by its unique identifier.
   * - `selectedPlatforms`: Returns an array of platforms that have been selected.
   * - `getAuthorizedPlatforms`: Returns an array of platforms that have authorization confirmed.
   */
  getters: {
    getPlatformById: (state) => (id) => {
      return state.platforms.find((platform) => platform.id === id);
    },
    selectedPlatforms: (state) => {
      return state.platforms.filter((p) => p.selected);
    },
    getAuthorizedPlatforms: (state) =>
      state.platforms.filter((p) => p.authorisation_confirmed),
  },
};
